/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @description
 *
 * @created at Tue Jan 17 2023
 **/

import { CSSProperties, useEffect, useState } from "react";
import { accordionStyle, cursorStyle } from "./private/emotion";
import useAccordion from "./useAccordion";

export interface AccordionProps extends React.HTMLAttributes<HTMLDivElement> {
	id: string;
	isParent?: boolean;
	maxHeight?: number;
	timer?: number;
}

export function Accordion({ id, onClick, isParent = true, timer = 300, maxHeight, children, ...rest }: AccordionProps) {
	const { data, handleSelectAccordion } = useAccordion();
	const isActive = !!data && Boolean(data[id]);
	const [autoMaxHeight, setAutoMaxHeight] = useState("0");
	const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
		onClick && onClick(e);
		handleSelectAccordion(id);
	};

	useEffect(() => {
		if (!data) return;

		let bodyHeight = 0;

		if (isParent) {
			const bodyElms = document.getElementById(id)?.querySelectorAll('[data-name="body"]');
			bodyElms?.forEach((item) => {
				const elm = item.lastChild as HTMLElement;
				if (data[id]) {
					bodyHeight += elm?.scrollHeight || 0;
				} else {
					bodyHeight = 0;
				}
			});
		} else {
			const elm = document.getElementById(id)?.querySelector('[data-name="body"]')?.lastChild as HTMLElement;
			bodyHeight = elm?.scrollHeight || 0;
		}
		const hasMaxHeight = `${data[id] ? (maxHeight ? maxHeight + "px" : "max-content") : "0"}`;
		const bodyMaxHeight = `${data[id] ? bodyHeight + "px" : "0"}`;
		setAutoMaxHeight(maxHeight ? hasMaxHeight : bodyMaxHeight);
	}, [isActive, children]);

	return (
		<div
			id={id}
			css={accordionStyle(!!data && Boolean(data[id]), isParent, id, timer, autoMaxHeight)}
			onClick={handleClick}
			{...rest}
		>
			{children}
		</div>
	);
}

function AccordionHeader({ children, id, ...rest }: React.HTMLAttributes<HTMLDivElement>) {
	const { data } = useAccordion();
	return (
		<div data-name="header" css={cursorStyle} {...rest} data-is-open={[data && data[String(id)]]}>
			{children}
		</div>
	);
}

function AccordionBody({
	children,
	bodyStyle,
	...rest
}: React.HTMLAttributes<HTMLDivElement> & { bodyStyle?: CSSProperties }) {
	useEffect(() => {}, [children]);
	return (
		<div data-name="body" className="body" style={bodyStyle}>
			<div {...rest}>{children}</div>
		</div>
	);
}

Accordion.Header = AccordionHeader;
Accordion.Body = AccordionBody;
